import React from "react";
import { Dropdown } from "react-bootstrap";
import { Plus, Utensils, LinkIcon, FileText, CheckSquare, ClipboardList, Trash2, Tag, User } from "lucide-react";

const CustomToggle = React.forwardRef(({ children, onClick, id }, ref) => (
    <div
        ref={ref}
        id={id}
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}
        style={{ cursor: "pointer", lineHeight: 0 }}
        className="text-muted p-1 rounded hover-bg-light d-inline-block"
    >
        {children}
    </div>
));

const EVENT_ACCENT_COLORS = ['#6366F1', '#3B82F6', '#8B5CF6', '#818CF8', '#7C3AED', '#2563EB'];

const DayColumn = ({
    day,
    isToday,
    handlers,
    editingState,
    enableEventCompletion,
    members = []
}) => {
    const {
        handleAdd,
        handleDelete,
        handleToggleTask,
        handleToggleEvent,
        handleMealRowClick,
        startEditing,
        handleSaveInlineEdit,
        setEditValue
    } = handlers;

    const { editingItem, editValue } = editingState;

    // Logic for Header Coloring
    // Priority: Marker > Holiday > Vacation > Default
    let headerBg = "transparent";
    let headerText = "#475569";
    let subLabel = null;

    if (day.marker) {
        headerBg = day.marker.color;
        // Adjust text color based on background if needed, simpler to keep dark for pastel colors
        headerText = "#1e293b";
        subLabel = day.marker.label;
    } else if (day.isPublicHoliday) {
        headerBg = "#fef2f2";
        headerText = "#991b1b";
        subLabel = "Feiertag";
    } else if (day.isSchoolVacation) {
        headerBg = "#f0fdf4";
        headerText = "#166534";
        subLabel = "Ferien";
    }

    // Track event index separately to cycle accent colors only across events (not meals)
    let eventIndex = 0;

    return (
        <div className={`day-card rounded shadow-sm d-flex flex-column h-100 ${isToday ? 'today-highlight' : ''} ${day.isPast ? 'past-day' : ''}`} style={{ backgroundColor: day.isPast ? '' : '#ffffff' }}>
            {/* Header */}
            <div className="p-2 border-bottom position-relative d-flex justify-content-center align-items-center" style={{ zIndex: 20, backgroundColor: headerBg, borderTopLeftRadius: '11px', borderTopRightRadius: '11px' }}>
                <div className="text-center">
                    <div className="fw-bold" style={{ color: isToday ? '#3b82f6' : headerText }}>{day.label}</div>
                    {subLabel && (
                        <div style={{ fontSize: '0.6rem', color: headerText, textTransform: 'uppercase', fontWeight: 'bold' }}>
                            {subLabel}
                        </div>
                    )}
                </div>
                <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                    <Dropdown>
                        <Dropdown.Toggle as={CustomToggle} id={`dropdown-add-${day.key}`}>
                            <Plus size={20} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" className="shadow-sm border-0">
                            <Dropdown.Item onClick={() => handleAdd(day.key, 'tasks')} className="small"><CheckSquare size={14} className="me-2" />Todo</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleAdd(day.key, 'events')} className="small"><ClipboardList size={14} className="me-2" />Event</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleAdd(day.key, 'meals')} className="small"><Utensils size={14} className="me-2" />Meal</Dropdown.Item>
                            <Dropdown.Item onClick={() => handleAdd(day.key, 'notes')} className="small"><FileText size={14} className="me-2" />Note</Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={() => handleAdd(day.key, 'marker')} className="small"><Tag size={14} className="me-2" />{day.marker ? "Markierung bearbeiten" : "Tag markieren"}</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>

            {/* Body */}
            <div className="p-2 flex-grow-1">
                {/* Schedule (Meals + Events) */}
                {day.scheduleItems.length > 0 && (
                    <div className="mb-3">
                        {day.scheduleItems.map(item => {
                            const isMeal = item.type === 'meals';
                            const accentColor = isMeal
                                ? '#F59E0B'
                                : (item.syncColor || EVENT_ACCENT_COLORS[eventIndex % EVENT_ACCENT_COLORS.length]);
                            if (!isMeal) eventIndex++;

                            return (
                                <div
                                    key={item.id}
                                    className={`d-flex align-items-center position-relative item-row ${isMeal ? 'item-card-meal' : 'item-card-event'}`}
                                    onClick={() => isMeal ? handleMealRowClick(day.key, item) : startEditing(day.key, item.type, item.original)}
                                    style={{
                                        borderLeft: `3px solid ${accentColor}`
                                    }}
                                    title={item.title}
                                >
                                    {isMeal ? (
                                        <div className="flex-grow-1 d-flex align-items-center overflow-hidden position-relative">
                                            <span className="fw-bold me-2" style={{ fontSize: '0.7rem', color: '#94A3B8' }}>{item.time}</span>
                                            <Utensils size={14} className="me-2" style={{ color: '#d97706' }} />
                                            <div className="text-truncate fw-semibold flex-grow-1" style={{ color: '#1E293B' }}>
                                                {item.title}
                                            </div>
                                            {item.original.externalRecipeUrl && (
                                                <span className="ms-1" title="Externes Rezept öffnen">
                                                    <LinkIcon size={12} className="text-primary" />
                                                </span>
                                            )}
                                            {(() => {
                                                // Note used here check logic in parent or pass boolean
                                                // Passing full object logic here might be tricky if it depends on 'plans' which is not passed
                                                // item.original should be enough if mapped correctly in parent
                                                // But for now, let's assume item.original has what we need or we pass it
                                                // Looking at WeeklyScheduleView, plans is used.
                                                // We should probably rely on item properties calculated in parent or pass plans?
                                                // Parent calculation:
                                                // const rId = fullPlan?.recipeId || item.original.recipeId || item.original.recipe_id;
                                                // The parent mapped 'scheduleItems' doesn't seem to include 'rId' explicitly.
                                                // Let's defer this specific check logic to a safer passed prop or just include it in parent mapping?
                                                // Parent mapping:
                                                // scheduleItems.push({ ..., original: meal })
                                                // So we still need `plans`.
                                                // To keep DayColumn pure, let's assume properties are enhanced in parent or we pass strict data.
                                                // For this refactor, I will copy the logic but we need 'plans' prop.
                                                // OR better: Update parent to compute this 'hasCheckifyRecipe' boolean and pass it in item.

                                                // For now, I'll pass 'plans' in handlers or props or just ignore for a sec?
                                                // Safe bet: Pass 'isCheckifyRecipe' as a prop on the item from the parent.
                                                // I will update WeeklyScheduleView to add this property to the item.
                                                return item.isCheckifyRecipe ? (
                                                    <span className="ms-1" title="Checkify Rezept öffnen">
                                                        <LinkIcon size={12} className="text-success" />
                                                    </span>
                                                ) : null;
                                            })()}

                                            {/* Delete Button for Meals */}
                                            <div
                                                className="delete-btn position-absolute top-50 end-0 translate-middle-y me-2 bg-white rounded-circle shadow-sm p-1 d-flex align-items-center justify-content-center"
                                                style={{ cursor: "pointer", right: "5px", zIndex: 10, width: '24px', height: '24px' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (window.confirm("Möchtest du diese Mahlzeit wirklich löschen?")) {
                                                        handleDelete('meals', item.original);
                                                    }
                                                }}
                                                title="Mahlzeit löschen"
                                            >
                                                <Trash2 size={12} className="text-danger" />
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            {enableEventCompletion && (
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input me-1 mt-0"
                                                    checked={item.original.isCompleted || false}
                                                    onChange={(e) => {
                                                        handleToggleEvent(item.original);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ cursor: "pointer", flexShrink: 0, width: '18px', height: '18px' }}
                                                />
                                            )}
                                            <div className="w-100 ps-1 overflow-hidden"
                                                style={{ opacity: item.original.isCompleted && enableEventCompletion ? 0.5 : 1, textDecoration: item.original.isCompleted && enableEventCompletion ? 'line-through' : 'none' }}
                                            >
                                                <div className="text-truncate fw-semibold" style={{ color: '#1E293B', lineHeight: '1.2' }}>
                                                    {item.title}
                                                </div>
                                                <div className="d-flex align-items-center mt-1" style={{ lineHeight: '1.1' }}>
                                                    <span className="fw-bold me-2" style={{ fontSize: '0.7rem', color: '#94A3B8' }}>
                                                        {item.time}{item.endTime ? ` – ${item.endTime}` : ''}
                                                    </span>
                                                    {item.linkedProfiles && item.linkedProfiles.length > 0 && (
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {item.linkedProfiles.map((p, idx) => (
                                                                <div key={idx} className="d-flex align-items-center">
                                                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: p.color || '#ccc' }} className="me-1"></div>
                                                                    <span style={{ fontSize: '0.7rem', color: '#64748B' }}>{p.displayName || p.firstName || p.name}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* Delete Button - Moved to outer container for correct positioning */}
                                    {!isMeal && (
                                        <div
                                            className="delete-btn position-absolute top-50 end-0 translate-middle-y me-2 bg-white rounded-circle shadow-sm p-1 d-flex align-items-center justify-content-center"
                                            style={{ cursor: "pointer", right: "5px", zIndex: 10, width: '24px', height: '24px' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (window.confirm("Möchtest du diesen Termin wirklich löschen?")) {
                                                    handleDelete('events', item.original);
                                                }
                                            }}
                                            title="Termin löschen"
                                        >
                                            <Trash2 size={12} className="text-danger" />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Snacks */}
                {day.snacks.length > 0 && (
                    <div className="mb-2 border-top pt-2">
                        {day.snacks.map(snack => (
                            <div
                                key={snack.id}
                                className="d-flex align-items-center text-muted position-relative item-row item-card-snack"
                                style={{ cursor: 'pointer' }}
                                onClick={() => startEditing(day.key, 'meals', snack.original)}
                                title={snack.title}
                            >
                                <Utensils size={12} className="me-2 text-warning" />
                                <span className="text-truncate fst-italic flex-grow-1">{snack.title}</span>

                                {/* Delete Button for Snacks */}
                                <div
                                    className="delete-btn position-absolute top-50 end-0 translate-middle-y me-2 bg-white rounded-circle shadow-sm p-1 d-flex align-items-center justify-content-center"
                                    style={{ cursor: "pointer", right: "5px", zIndex: 10, width: '24px', height: '24px' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (window.confirm("Möchtest du diesen Snack wirklich löschen?")) {
                                            handleDelete('meals', snack.original);
                                        }
                                    }}
                                    title="Snack löschen"
                                >
                                    <Trash2 size={12} className="text-danger" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Tasks */}
                {day.tasks.length > 0 && (
                    <div className="mb-3 border-top pt-2">
                        {day.tasks.map(task => {
                            const isInlineEditing = editingItem.id === task.id && editingItem.type === "tasks";
                            return (
                                <div key={task.id} className="d-flex align-items-center position-relative item-row item-card-task" title={task.title || task.description}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input me-2 mt-0"
                                        checked={task.isDone || false}
                                        onChange={() => handleToggleTask(task, day.key)}
                                        disabled={task.isVirtual || (typeof task.id === 'string' && task.id.startsWith("gen-"))}
                                        style={{ cursor: "pointer", flexShrink: 0, width: '18px', height: '18px' }}
                                    />
                                    <div className="flex-grow-1 text-truncate" onClick={() => startEditing(day.key, 'tasks', task)}>
                                        {isInlineEditing ? (
                                            <input
                                                type="text"
                                                value={editValue}
                                                onChange={(e) => setEditValue(e.target.value)}
                                                onBlur={handleSaveInlineEdit}
                                                onKeyDown={(e) => e.key === "Enter" && handleSaveInlineEdit()}
                                                autoFocus
                                                className="form-control form-control-sm border-0 bg-transparent p-0"
                                                style={{ fontSize: "inherit" }}
                                            />
                                        ) : (
                                            <span className={`${task.isDone ? "text-decoration-line-through text-muted" : ""}`} style={{ cursor: "pointer", fontWeight: 600, color: '#1E293B' }}>{task.title || task.description}</span>
                                        )}
                                    </div>

                                    {/* Assignee Avatar */}
                                    {(() => {
                                        if (!task.assigned_to) return null;
                                        const assignedMember = members.find(m => m.id === task.assigned_to); // Match by Profile ID
                                        if (!assignedMember) return null;
                                        return (
                                            <div className="ms-1 flex-shrink-0" title={`Assigned to ${assignedMember.displayName}`}>
                                                {assignedMember.avatarUrl ? (
                                                    <img src={assignedMember.avatarUrl} alt={assignedMember.displayName} className="rounded-circle border" style={{ width: 16, height: 16, objectFit: 'cover' }} />
                                                ) : (
                                                    <div className="rounded-circle d-flex align-items-center justify-content-center text-white border" style={{ width: 16, height: 16, backgroundColor: assignedMember.color || '#6c757d', fontSize: '8px', fontWeight: 'bold' }}>
                                                        {assignedMember.displayName?.substring(0, 2).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Delete Button for Tasks - Replaces 'x' button */}
                                    <div
                                        className="delete-btn bg-white rounded-circle shadow-sm p-1 d-flex align-items-center justify-content-center"
                                        style={{ cursor: "pointer", width: '24px', height: '24px', marginLeft: 'auto' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm("Möchtest du dieses ToDo wirklich löschen?")) {
                                                handleDelete('tasks', task);
                                            }
                                        }}
                                        title="ToDo löschen"
                                    >
                                        <Trash2 size={12} className="text-danger" />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Notes */}
                {day.notes.length > 0 && (
                    <div className="mb-2 border-top pt-2">
                        {day.notes.map(note => (
                            <div
                                key={note.id}
                                className="p-1 mb-1 rounded cursor-pointer text-truncate position-relative item-row item-card-note"
                                style={{ fontWeight: 600, color: '#1E293B' }}
                                onClick={() => startEditing(day.key, 'notes', note)}
                                title={note.title || note.content}
                            >
                                {note.title || note.content}
                                {/* Delete Button for Notes */}
                                <div
                                    className="delete-btn position-absolute top-50 end-0 translate-middle-y me-1 bg-white rounded-circle shadow-sm p-1 d-flex align-items-center justify-content-center"
                                    style={{ cursor: "pointer", right: "2px", zIndex: 10, width: '20px', height: '20px' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (window.confirm("Möchtest du diese Notiz wirklich löschen?")) {
                                            handleDelete('notes', note);
                                        }
                                    }}
                                    title="Notiz löschen"
                                >
                                    <Trash2 size={10} className="text-danger" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DayColumn;
